import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'

import { MarathonMark } from '@/components/landing/marathon-mark'
import { cn } from '@/lib/utils'

interface SiteHeaderProps {
    /* overlay = transparent over a hero, reveals the bar on scroll (home).
       solid   = bar is always visible from the top (sub-pages). */
    variant?: 'overlay' | 'solid'
    /* hides the background bar (e.g. sub-pages that only want the pill nav). */
    showBackground?: boolean
    /* hides the framing chrome — the left logo and right CTA — leaving only
       the centre pill nav. */
    showChrome?: boolean
}

const pageLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' }
]

export const SiteHeader: FC<SiteHeaderProps> = ({
    variant = 'overlay',
    showBackground = false,
    showChrome = false
}): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        if (variant === 'solid') return
        const onScroll = () => {
            setScrolled(window.scrollY > window.innerHeight * 0.85)
        }
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [variant])

    // The framing bar (background + logo + CTA) shows on solid pages or once
    // scrolled past the hero. The centre pill is ALWAYS visible.
    const barVisible = variant === 'solid' || scrolled

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            {/* Background bar — fades in with the framing chrome */}
            {showBackground && (
                <div
                    className={cn(
                        'absolute inset-0 border-b border-white/10 bg-[#0b0b0d]/80 backdrop-blur-xl transition-opacity duration-500',
                        barVisible ? 'opacity-100' : 'opacity-0'
                    )}
                />
            )}

            <div className='relative mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8'>
                {/* Left: logo (part of the framing chrome) */}
                {showChrome && (
                    <Link
                        href='/'
                        aria-label='Marathon home'
                        className={cn(
                            'flex items-center gap-2.5 text-white transition-opacity duration-500',
                            barVisible
                                ? 'opacity-100'
                                : 'pointer-events-none opacity-0'
                        )}
                    >
                        <MarathonMark className='h-5 w-5' />
                        <span className='text-sm font-bold tracking-[0.22em]'>
                            MARATHON
                        </span>
                    </Link>
                )}

                {/* Centre: the small pill nav — always visible */}
                <nav className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
                    <div className='flex items-center gap-1 rounded-full border border-white/15 bg-white/10 p-1 backdrop-blur-xl'>
                        {pageLinks.map((link) => {
                            const active = router.pathname === link.href
                            const isHome = link.href === '/'
                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    aria-label={isHome ? 'Home' : undefined}
                                    className={cn(
                                        'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors',
                                        active
                                            ? 'bg-[#E94E1B] text-white'
                                            : 'text-white/75 hover:text-white'
                                    )}
                                >
                                    {isHome ? (
                                        <>
                                            <MarathonMark
                                                className='h-4 w-4'
                                                color={
                                                    active ? '#ffffff' : undefined
                                                }
                                            />
                                            <span className='tracking-[0.18em]'>
                                                MARATHON
                                            </span>
                                        </>
                                    ) : (
                                        link.label
                                    )}
                                </Link>
                            )
                        })}
                    </div>
                </nav>

                {/* Right: CTA (part of the framing chrome) */}
                {showChrome && (
                    <Link
                        href='/#download'
                        className={cn(
                            'hidden rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all duration-500 hover:bg-white/90 sm:inline-flex',
                            barVisible
                                ? 'opacity-100'
                                : 'pointer-events-none opacity-0'
                        )}
                    >
                        Get the app
                    </Link>
                )}
            </div>
        </header>
    )
}
